// ============================================================
// Input state — captures keyboard + mouse for flight controls
// ============================================================

export interface InputState {
  readonly forward: boolean;
  readonly backward: boolean;
  readonly left: boolean;
  readonly right: boolean;
  readonly up: boolean;
  readonly down: boolean;
  readonly mouseX: number;
  readonly mouseY: number;
  readonly fireMissile: boolean;
  readonly fireGun: boolean;
  readonly boost: boolean;
  readonly flare: boolean;
  readonly lockMode: boolean;
  readonly deployFlare: boolean;
}

export interface KeyBindings {
  forward: string;
  backward: string;
  left: string;
  right: string;
  up: string;
  down: string;
  boost: string;
  flare: string;
  lockMode: string;
  deployFlare: string;
}

const DEFAULT_BINDINGS: KeyBindings = {
  forward: 'KeyW',
  backward: 'KeyS',
  left: 'KeyA',
  right: 'KeyD',
  up: 'Space',
  down: 'ShiftLeft',
  boost: 'KeyE',
  flare: 'KeyQ',
  lockMode: 'ControlLeft',
  deployFlare: 'KeyX',
};

export class InputHandler {
  private keys = new Set<string>();
  private _mouseX = 0;
  private _mouseY = 0;
  private _leftClick = false;
  private _rightClick = false;
  private _locked = false;
  readonly bindings: KeyBindings;

  constructor(private readonly canvas: HTMLCanvasElement) {
    // Load saved bindings or use defaults
    const saved = localStorage.getItem('skyfall_keybindings');
    this.bindings = saved ? { ...DEFAULT_BINDINGS, ...JSON.parse(saved) } : { ...DEFAULT_BINDINGS };
    this.bindEvents();
  }

  private bindEvents(): void {
    window.addEventListener('keydown', (e) => {
      this.keys.add(e.code);
    });
    window.addEventListener('keyup', (e) => {
      this.keys.delete(e.code);
    });

    // Skip pointer lock on touch devices
    if (!('ontouchstart' in window)) {
      this.canvas.addEventListener('click', () => {
        if (!this._locked) {
          this.canvas.requestPointerLock();
        }
      });
    }

    document.addEventListener('pointerlockchange', () => {
      this._locked = document.pointerLockElement === this.canvas;
      if (!this._locked) {
        this._mouseX = 0;
        this._mouseY = 0;
      }
    });

    document.addEventListener('mousemove', (e) => {
      if (!this._locked) return;
      this._mouseX = Math.max(-1, Math.min(1, e.movementX / 10));
      this._mouseY = Math.max(-1, Math.min(1, e.movementY / 10));
    });

    this.canvas.addEventListener('mousedown', (e) => {
      if (e.button === 0) this._leftClick = true;
      if (e.button === 2) this._rightClick = true;
    });
    this.canvas.addEventListener('mouseup', (e) => {
      if (e.button === 0) this._leftClick = false;
      if (e.button === 2) this._rightClick = false;
    });

    this.canvas.addEventListener('contextmenu', (e) => e.preventDefault());
  }

  get isLocked(): boolean {
    return this._locked;
  }

  rebind(action: keyof KeyBindings, code: string): void {
    this.bindings[action] = code;
    localStorage.setItem('skyfall_keybindings', JSON.stringify(this.bindings));
  }

  private isKey(code: string): boolean {
    // Handle Shift/Ctrl which have Left/Right variants
    if (code === 'ShiftLeft') return this.keys.has('ShiftLeft') || this.keys.has('ShiftRight');
    if (code === 'ControlLeft') return this.keys.has('ControlLeft') || this.keys.has('ControlRight');
    return this.keys.has(code);
  }

  getState(): InputState {
    const b = this.bindings;
    const state: InputState = {
      forward: this.isKey(b.forward),
      backward: this.isKey(b.backward),
      left: this.isKey(b.left),
      right: this.isKey(b.right),
      up: this.isKey(b.up),
      down: this.isKey(b.down),
      mouseX: this._mouseX,
      mouseY: this._mouseY,
      fireMissile: this._leftClick,
      fireGun: this._rightClick,
      boost: this.isKey(b.boost),
      flare: this.isKey(b.flare),
      lockMode: this.isKey(b.lockMode),
      deployFlare: this.isKey(b.deployFlare),
    };

    this._mouseX *= 0.5;
    this._mouseY *= 0.5;

    return state;
  }
}

/** Convert e.code to a readable label */
export function keyCodeToLabel(code: string): string {
  if (code.startsWith('Key')) return code.slice(3);
  if (code.startsWith('Digit')) return code.slice(5);
  const map: Record<string, string> = {
    Space: 'Space', ShiftLeft: 'Shift', ShiftRight: 'Shift',
    ControlLeft: 'Ctrl', ControlRight: 'Ctrl',
    AltLeft: 'Alt', AltRight: 'Alt',
    ArrowUp: 'Up', ArrowDown: 'Down', ArrowLeft: 'Left', ArrowRight: 'Right',
    Tab: 'Tab', Enter: 'Enter', Backspace: 'Backspace',
  };
  return map[code] ?? code;
}
